//This function handles roles/permission and access of the sidebar admin menus
//Super admin will access all

import { getStorage } from "./storage";
import {
  ROLE_NATIONAL_INVENTORY,
  ROLE_EMERGENCY_ENDORSEMENT,
} from "../constants/ROLES";

//Normal admin access will be determined by the role admin has granted you
export const defineRole = ({ roles = [], menu, operation, pass = false }) => {
  try {
    const userLoggedIn = JSON.parse(
      localStorage.getItem(process.env.REACT_APP_NAME) || "{}"
    );

    const { role = "" } = userLoggedIn;

    let accessedMenus = [];

    roles.forEach((roleObj) => {
      const { permissions } = roleObj;
      const { menus, operations } = permissions;

      // Support multiple operations check
      let splitOperation = operation.split(",");

      splitOperation.forEach((op) => {
        if (operations[op]) {
          if (Array.isArray(menu)) {
            menu.forEach((mEl) => {
              if (operations[op]?.includes(mEl) && menus?.includes(mEl)) {
                accessedMenus.push(mEl);
              }
            });
          } else if (operations[op]?.includes(menu) && menus?.includes(menu)) {
            accessedMenus.push(menu);
          }
        }
      });
    });

    if (role === "superAdmin" || pass) {
      return true;
    } else if (Array.isArray(menu)) {
      return menu.find((mEl) => accessedMenus.includes(mEl));
    } else {
      return accessedMenus.includes(menu);
    }
  } catch (error) {
    return false;
  }
};

// Inventory scope follows the account's own blood bank, so somebody posted
// to a hospital sees that hospital and nothing else, and the only accounts
// that ever saw national figures were the ones with no blood bank at all.
// This role lifts that bound for the inventory dashboard and nothing else:
// it carries no write operation, so it can be handed to a monitoring desk
// without also handing over a facility's stock.
export const hasNationalInventory = async () => {
  const user = await getStorage();

  if (user?.role === "superAdmin") return true;

  return !!defineRole({
    roles: user?.accessRole || [],
    menu: ROLE_NATIONAL_INVENTORY,
    operation: "read",
  });
};

// Whether this account can endorse an emergency release at its own
// hospital. The server checks this again before writing: this only decides
// whether the button is there to press.
export const canEndorseEmergency = async () => {
  const user = await getStorage();

  if (user?.role === "superAdmin") return true;

  return !!defineRole({
    roles: user?.accessRole || [],
    menu: ROLE_EMERGENCY_ENDORSEMENT,
    operation: "update",
  });
};

export const getDefaultPage = () => {
  try {
    let page;
    const user = JSON.parse(
      localStorage.getItem(process.env.REACT_APP_NAME) || "{}"
    );

    for (let role of user?.accessRole || []) {
      if (role?.defaultPage) {
        page = role?.defaultPage;
        break;
      }
    }

    return page;
  } catch (error) {
    return;
  }
};

// A centre that stands for all of them. An account assigned it is central
// service rather than one site, so its queries carry no centre filter at
// all - the same shape superAdmin already gets. Matched on name because it
// is a BloodCenter record like any other, created and renamed in the UI.
export const ALL_CENTERS = "BTD CENTRAL SERVICE";

export const hasAllCenters = (center) => {
  const list = Array.isArray(center) ? center : [center];

  return list.some((el) => {
    const name = typeof el === "string" ? el : el?.name;

    return String(name || "").trim().toUpperCase() === ALL_CENTERS;
  });
};

export const defineRoleCenterSite = async () => {
  const { center, centerSite, role, hospital, stock, allowedBloodBank, multipleAllowedBloodBank=[] } =
    await getStorage();

  if (role === "superAdmin") {
    return {};
  }

  // Central service sees every centre, so the centre and site filters come
  // off entirely rather than being widened to a list that would go stale as
  // centres are added.
  if (hasAllCenters(center)) {
    return {
      centerRole: undefined,
      centerSiteRole: undefined,
      hospitalRole: hospital ? hospital?._id || hospital : undefined,
      stock: stock ? stock : undefined,
      allowedBloodBank: allowedBloodBank?._id || allowedBloodBank || undefined,
      allowedBloodBankDetails: allowedBloodBank,
      multipleAllowedBloodBank,
    };
  }

  const extractId = (val) => {
    if (!val) return undefined;
    if (Array.isArray(val)) {
      const ids = val.map((v) => v?._id || v?.value || v).filter(Boolean);
      return ids.length ? ids : undefined;
    }
    return val?._id || val?.value || val;
  };

  return {
    centerRole: extractId(center),
    centerSiteRole: extractId(centerSite),
    hospitalRole: hospital ? hospital?._id || hospital : undefined,
    stock: stock ? stock : undefined,
    allowedBloodBank: allowedBloodBank?._id || allowedBloodBank || undefined,
    allowedBloodBankDetails: allowedBloodBank,
    multipleAllowedBloodBank
  };
};
